import {
    Box,
    Button,
    Flex,
    Heading,
    Spinner,
    Stack,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr
} from "@chakra-ui/react";
import * as React from "react";
import {useNavigate} from "react-router-dom";
import {useInfiniteQuery, useMutation} from "@tanstack/react-query";
import {AddIcon, DeleteIcon, EditIcon, ViewIcon} from "@chakra-ui/icons";
import {useState} from "react";
import {getCategoriesNew, deleteCategory} from "../../../../data/api/Customer";
import {CategoryModelNew} from "../../../../data/model/Category";
import {Image} from "@chakra-ui/react";
import {CustomModal} from "../../../../components/molecules/ModalComponent/ModalComponent";
import {CategoryDetailPage} from "./CategoryDetailPage";
import {useDialogStore} from "../../../../store/useAlertDialog";

interface CategoriesResponse {
    isLastPage: boolean;
    totPage: number;
    currentPage: number;
    data: CategoryModelNew[];
}

export default function CategoriesPage() {
    const navigate = useNavigate();

    const {
        data,
        isLoading,
        isError,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage
    } = useInfiniteQuery<CategoriesResponse, Error>({
        queryKey: ["categories"],
        initialPageParam: 0,
        queryFn: async ({ pageParam = 0 }) => {
            const res = await getCategoriesNew({ page: pageParam as number });
            return res?.data.data as CategoriesResponse;
        },
        getNextPageParam: (lastPage) =>
            lastPage.isLastPage ? undefined : lastPage.currentPage + 1
    });

    const categories: CategoryModelNew[] =
        data?.pages.flatMap((page) => page.data) ?? [];

    const userType = localStorage.getItem("userType");

    return (
        <Box p={6}>
            <Flex justify="space-between" align="center" mb={4}>
                <Heading size="lg">Tutte le categorie</Heading>
                {userType === "admin" && (
                    <Button
                        leftIcon={<AddIcon />}
                        colorScheme="teal"
                        onClick={() => navigate("/products/category/add")}
                    >
                        Aggiungi
                    </Button>
                )}
            </Flex>

            {isLoading && (
                <Flex justify="center" py={10}>
                    <Spinner size="xl" />
                </Flex>
            )}

            {isError && (
                <Text color="red.500" textAlign="center">
                    Errore nel caricamento
                </Text>
            )}

            {!isLoading && (
                <Box overflowX="auto">
                    <Table variant="simple">
                        <Thead>
                            <Tr>
                                <Th>Immagine</Th>
                                <Th>Nome</Th>
                                <Th>Descrizione</Th>
                                <Th>Azioni</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {categories?.map((cat) => (
                                <CategoryRow
                                    key={cat.id}
                                    category={cat}
                                    userType={userType}
                                    onEdit={() => navigate(`/products/category/edit/${cat.id}`)}
                                />
                            ))}
                        </Tbody>
                    </Table>
                </Box>
            )}

            <Flex justify="center" mt={4}>
                {hasNextPage ? (
                    <Button
                        onClick={() => fetchNextPage()}
                        isLoading={isFetchingNextPage}
                    >
                        Carica altro
                    </Button>
                ) : (
                    <Text color="gray.500">Nessun'altra categoria</Text>
                )}
            </Flex>
        </Box>
    );
}

function CategoryRow({ category, userType, onEdit }: { category: CategoryModelNew; userType: string | null; onEdit: () => void }) {
    const [isOpen, setOpen] = useState(false);
    const isAdmin = userType === 'admin';

    const deleteMutation = useMutation({
        mutationFn: (id: string) => deleteCategory(id),
        onSuccess: () => window.location.reload()
    });

    const {showDialog, closeDialog} = useDialogStore();

    return (
        <>
            <Tr>
                <Td>
                    <Image
                        boxSize="64px"
                        borderRadius="16px"
                        objectFit="cover"
                        src={category?.image?.url}
                        alt={category?.name}
                    />
                </Td>
                <Td>{category?.name}</Td>
                <Td maxWidth="300px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">{category?.description}</Td>
                <Td>
                    <Stack direction="row" spacing={2}>
                        <Button
                            size="sm"
                            leftIcon={<ViewIcon />}
                            onClick={() => setOpen(true)}
                        >
                            Apri
                        </Button>
                        {isAdmin && <Button size="sm" leftIcon={<EditIcon />} onClick={onEdit}>
                            Modifica
                        </Button>}
                        {isAdmin && <Button
                            size="sm"
                            leftIcon={<DeleteIcon />}
                            color={'red'}
                            onClick={() => showDialog({
                                title: "Elimina categoria",
                                description: "Sei sicuro di voler eliminare questa categoria?\nI prodotti associati non apparteranno più alla categoria",
                                cancelButtonText: "Annulla",
                                confirmButtonText: "Elimina",
                                onPressCancelButton: () => closeDialog(),
                                onConfirmButtonClick: () => {
                                    deleteMutation.mutate(category?.id || '')
                                }
                            })}
                        >
                            Elimina
                        </Button>}
                    </Stack>
                </Td>
            </Tr>

            <CustomModal
                isOpen={isOpen}
                onClose={() => setOpen(false)}
                content={<CategoryDetailPage id={category.id} />}
            />
        </>
    );
}

/*
import {Box, HStack, Tbody, Text, VStack} from "@chakra-ui/react";
import * as React from "react";
import {TableComponent} from "../Products/ProductsPage";
import {scrollbarCss} from "../../../../theme/theme";
import {AddIcon} from "@chakra-ui/icons";
import {useNavigate} from "react-router-dom";
import {useQuery} from "@tanstack/react-query";
import {getCategoriesNew} from "../../../../data/api/Customer";
import {CategoryModelNew} from "../../../../data/model/Category";
import {
    CategoriesTableBodyLoading,
    CategoriesTableComponent
} from "../../../../components/organisms/CategoriesTableComponent/CategoriesTableComponent";
import {useState} from "react";
import {usePaginationData} from "../../../../utils/Utils";
import {
    PaginationInputComponent
} from "../../../../components/molecules/PaginationInputComponent/PaginationInputComponent";

export default function CategoriesPage() {
    const navigate = useNavigate();
    const [page, setPage] = useState(1);

    // TODO: filtraggio
    const responseAll = useQuery({
        queryKey: ['get-categories-all', page],
        queryFn: () => getCategoriesNew({page: page - 1}),
        refetchOnWindowFocus: "always"
    });

    const {
        data: categoriesAll,
        totPage,
        isLoading,
        refetch
    } = usePaginationData<CategoryModelNew[]>(responseAll);

    const userType = localStorage.getItem("userType");

    return (
        <VStack w={"100%"} alignItems={"flex-start"} overflow={"auto"}
                css={scrollbarCss({marginLeft: 0, marginRight: 0})}>

            <HStack pt={"24px"}>
                <Text fontSize={"24px"} fontWeight={"bold"} ms={"24px"}>Tutte le categorie</Text>

                <PaginationInputComponent setPage={setPage} page={page} totPage={totPage}/>
            </HStack>

            <Box mt={"18px"} mx={"24px"} alignSelf={"stretch"} backgroundColor={"surfaceContainer.light"}
                 borderRadius={"16px"} overflowX={"hidden"} mb={"72px"}
                 css={scrollbarCss({marginLeft: 0, marginRight: 0})}>
                <TableComponent body={isLoading ? <CategoriesTableBodyLoading isAllCategories={true}/> :
                    <CategoriesTableBody
                        data={categoriesAll || []}
                        isAllCategories={true}
                        doRefetch={refetch}
                        baseKey={'all_categories_row_index'}/>}
                                headers={["", "Nome", "Descrizione", ""]}/>
            </Box>

            {userType === "admin" &&
                <HStack cursor={"pointer"} h={"64px"} px={"24px"} borderRadius={"24px"} position={"absolute"}
                        alignItems={"center"}
                        justifyContent={"center"} bottom={"16px"} right={"16px"} backgroundColor={"secondary.200"}
                        _hover={{backgroundColor: "tertiary.200"}} onClick={() => {
                    navigate("/products/category/add");
                }}>
                    <AddIcon w={"16px"} h={"16px"} alignSelf={"center"}/>
                    <Text ms={"8px"} fontWeight={"bold"}>Aggiungi</Text>
                </HStack>}
        </VStack>
    );
}

const CategoriesTableBody = ({data, baseKey, isAllCategories = false, doRefetch}: {
    data?: CategoryModelNew[],
    baseKey?: string,
    isAllCategories?: boolean,
    doRefetch: () => void
}) => {
    return <Tbody>
        {(data?.length ?? 0) > 0 && data?.map((value, index) => {
            const {id, name, description, image} = value || {};
            return <CategoriesTableComponent key={`${baseKey}_${index}`}
                                             id={id}
                                             isAllCategories={isAllCategories}
                                             src={image?.url}
                                             name={name}
                                             doRefetch={doRefetch}
                                             description={description}/>;
        })}
    </Tbody>
}
*/
